import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { recipeCardTransformer } from 'src/models/Recipe/RecipeCard'
import {
  Day,
  Ingredient,
  MenuDay,
  MenuMoment,
  Moment,
  Recipe,
  Unbreakable,
} from 'src/types/fam-graphql'
import { breadcrumbTransformer } from 'src/models/Breadcrumb/breadcrumbTransformer'
import { colors } from 'src/theme'
import { tagCommanderTransformer } from 'src/models/TagCommander/tagCommanderTransformer'
import { TcPageTemplateNames } from 'src/configuration/tagcommander'
import { useRouter } from 'next/router'
import { getDayNumber } from 'src/utils/TimeUtils'
import { PayloadAction } from '@reduxjs/toolkit'

import {
  recipeCoverTransformer,
  recipeIngredientsTransformer,
  recipeIntroductionTransformer,
  recipeSeoTransformer,
  recipeStepsTransformer,
  recipeTagsTransformer,
} from '../models/Recipe'
import { crossLinkTransformer } from '../models/Crosslink'
import { actions, selectors } from '../redux'
import { RecipeTemplateProps } from '../templates/Recipe'
import Router from '../routes/router'
import routes from '../routes/definitions'
import { dayQueryTransformer, onlyTwoDecimals } from '../utils/RecipeUtils'
import { useCacheImage, useOfflineRecipes } from '../utils/CacheUtils'
import { childrenRecipesCardListTransformer } from '../models/Recipes'
import { secureSanitize } from '../utils/sanitizeUtils'

import { useSsoUrl } from './useSsoUrl'
import usePWAInstallPrompt from './usePWAInstallPrompt'

function hasUnbreakableIngredients(ingredients?: Ingredient[] | null) {
  if (!ingredients) {
    return
  }
  return (
    ingredients?.findIndex(
      (ing) => ing.unbreakable === Unbreakable.Complete
    ) !== -1
  )
}

function minUnbreakableIngredients(
  ingredients?: Ingredient[] | null,
  portions = 1
) {
  if (!ingredients) return 1

  for (let people = 1; people < 6; people++) {
    const multiplicationFactor = people / portions
    if (
      ingredients
        ?.map((ingredient) => {
          let quantity =
            multiplicationFactor *
            (ingredient?.ingredient_recipe?.quantity ?? 0)
          if (ingredient.unbreakable === Unbreakable.Partial) {
            quantity = Math.round(quantity)
          }
          return onlyTwoDecimals(quantity)
        })
        ?.findIndex((quantity) => quantity === 0) === -1
    ) {
      return people
    }
  }

  return 1
}

const hideFavoriteOnEnv =
  process.env.HIDE_RECIPE_STAR === 'true' ||
  process.env.HIDE_RECIPE_STAR === '1'

const useRecipe = () => {
  usePWAInstallPrompt()
  const dispatch = useDispatch()
  const recipe = useSelector(selectors.recipe.recipe)
  const side = useSelector(selectors.recipe.side)
  const isOffline = useSelector(selectors.app.isOffline)
  const otherRecipesOnPlanningDay = useSelector(
    selectors.recipe.recipesOnPlanningDay
  )
  const filters = useSelector(selectors.recipes.filters)
  const fromPlanningSelector = useSelector(selectors.recipe.fromPlanning)
  const daySelector = useSelector(selectors.recipe.day)
  const currentSeason = useSelector(selectors.app.currentSeason)
  const momentSelector = useSelector(selectors.recipe.moment)
  const favoriteRecipesIds = useSelector(selectors.user.favoriteRecipesIds)
  const [selectedSide, setSelectedSide] = useState('course')
  const planning = useSelector(selectors.user.currentPlanReordered)
  const isUserInit = useSelector(selectors.user.isInit)
  const isConnected = useSelector(selectors.user.isConnected)

  useEffect(() => {
    if (isUserInit && isConnected && !planning) {
      dispatch(actions.planning.load())
    }
  }, [isUserInit, isConnected, planning, dispatch])

  const nextRouter = useRouter()
  const dayFromQuery =
    nextRouter?.query?.jour &&
    getDayNumber(
      secureSanitize((nextRouter?.query?.jour as string) ?? '', true)
    )
  const momentFromQuery = secureSanitize(
    (nextRouter?.query?.moment as string) ?? '',
    true
  )
  const isFromPlanningFromQuery = secureSanitize(
    (nextRouter?.query?.fromPlanning as string) ?? '',
    true
  )
  const isFromHebdoFromQuery = secureSanitize(
    (nextRouter?.query?.fromHebdo as string) ?? '',
    true
  )
  const earlierPlanningIdFromQuery = secureSanitize(
    Router.router?.query?.earlierPlanningId as string,
    true
  )
  const rawDayFromQuery = secureSanitize(
    Router.router?.query?.jour as string,
    true
  )
  const dayTranslated = dayQueryTransformer(rawDayFromQuery ?? '') // Translate "jour_1" to "DAY_1" to match the API format

  const isParentRecipe = useMemo(() => {
    return (
      !!recipe?.parent &&
      recipe?.children_recipes?.length &&
      recipe?.children_recipes?.length > 0
    )
  }, [recipe?.parent, recipe?.children_recipes])

  const parentRecipeSlug = secureSanitize(
    Router.router?.query?.parent as string,
    true
  )

  const otherInPlanningMeal = useMemo(() => {
    if (
      dayTranslated &&
      momentFromQuery &&
      planning?.menu
        ?.find((m: MenuDay) => m.day === dayTranslated)
        ?.moments?.find((m: MenuMoment) => m?.moment === momentFromQuery)
        ?.meal?.find((r: Recipe) => r?.id === recipe?.id)
    ) {
      return planning?.menu
        ?.find((item: MenuDay) => item.day === dayTranslated)
        ?.moments?.filter((item: MenuMoment) => {
          return item.moment === momentFromQuery
        })[0]
        .meal?.filter((r) => r?.id !== recipe?.id)
    }

    return null
  }, [dayTranslated, momentFromQuery, planning?.menu, recipe?.id])

  useEffect(() => {
    if (otherInPlanningMeal) {
      dispatch(actions.recipe.setFromPlanning(true))
      dispatch(actions.recipe.setDay(dayTranslated))
      dispatch(actions.recipe.setMoment(momentFromQuery))
      if (otherInPlanningMeal)
        dispatch(
          actions.recipe.setOtherRecipesOnPlanningMeal(otherInPlanningMeal)
        )
    } else {
      dispatch(actions.recipe.setFromPlanning(false))
      dispatch(actions.recipe.setDay(null))
      dispatch(actions.recipe.setMoment(null))
    }
  }, [dayTranslated, dispatch, momentFromQuery, otherInPlanningMeal])

  const offlineMainImage = useCacheImage(recipe?.image ?? '')
  const offlineSideImage = useCacheImage(side?.image ?? '')
  const sameDayRecipes = useOfflineRecipes(
    otherInPlanningMeal ?? otherRecipesOnPlanningDay
  )

  const toDisplayRecipe = useMemo(() => {
    switch (selectedSide) {
      case 'course':
        return {
          ...recipe,
          ...(isOffline && offlineMainImage && { image: offlineMainImage }),
        } as Recipe
      case 'side':
        return {
          ...side,
          ...(isOffline && offlineSideImage && { image: offlineSideImage }),
        } as Recipe
      default:
        return {
          ...recipe,
          ...(isOffline && offlineMainImage && { image: offlineMainImage }),
        } as Recipe
    }
  }, [
    recipe,
    side,
    selectedSide,
    isOffline,
    offlineMainImage,
    offlineSideImage,
  ])
  const settings = useSelector(selectors.settings.list)
  const preferences = useSelector(selectors.user.preferences)
  const { t } = useTranslation()
  const isKeycloakProcessDone = useSelector(
    selectors.user.isKeycloakProcessDone
  )
  const day = useMemo(() => {
    return daySelector || dayFromQuery
  }, [dayFromQuery, daySelector])

  const moment = useMemo(() => {
    return momentSelector || momentFromQuery
  }, [momentFromQuery, momentSelector])
  const userLoggedIn = !!useSelector(selectors.user.keycloakToken)

  const hideFavorite = useMemo(
    () => !isKeycloakProcessDone || hideFavoriteOnEnv,
    [isKeycloakProcessDone]
  )

  const isFromPlanning = useMemo(
    () => fromPlanningSelector || isFromPlanningFromQuery === 'true',
    [fromPlanningSelector, isFromPlanningFromQuery]
  )

  const isFromHebdo = useMemo(() => isFromHebdoFromQuery === 'true', [
    isFromHebdoFromQuery,
  ])

  const constrainedPeopleCount = useRef(
    toDisplayRecipe?.unbreakable !== Unbreakable.Complete &&
      hasUnbreakableIngredients(toDisplayRecipe?.ingredients)
  )

  const peopleSteps = useRef(
    (constrainedPeopleCount.current && toDisplayRecipe?.portions) || 1
  )

  const minPortions = useRef(
    Math.max(
      toDisplayRecipe?.min_portions ?? 1,
      minUnbreakableIngredients(
        toDisplayRecipe?.ingredients,
        preferences?.portions ?? toDisplayRecipe?.portions ?? 2
      )
    )
  )

  const comesFromPlanning = useMemo(() => {
    return !!(dayFromQuery && momentFromQuery)
  }, [dayFromQuery, momentFromQuery])

  const [ingredientsPeople, setIngredientsPeople] = useState(
    recipe?.unbreakable === Unbreakable.Complete
      ? toDisplayRecipe?.portions ?? 2
      : isFromPlanning
      ? preferences?.portions ?? 2
      : toDisplayRecipe?.portions ?? 2
  )
  const [isStepByStepOpen, setIsStepByStepOpen] = useState(false)

  const onAddPersonHandler = useCallback(() => {
    setIngredientsPeople((prev) => prev + peopleSteps.current)
  }, [setIngredientsPeople])

  const onRemovePersonHandler = useCallback(() => {
    setIngredientsPeople((prev) => prev - peopleSteps.current)
  }, [setIngredientsPeople])

  const toggleStepByStepHandler = useCallback(() => {
    setIsStepByStepOpen((prev) => !prev)
  }, [setIsStepByStepOpen])

  const onSelectSwap = useCallback(
    (side: string) => {
      setSelectedSide(side)
    },
    [setSelectedSide]
  )

  const isFavorite = useMemo(
    () => !!recipe?.id && favoriteRecipesIds?.includes(recipe.id),
    [recipe?.id, favoriteRecipesIds]
  )

  const toggleFavorites = useCallback(() => {
    if (recipe?.id) {
      if (isFavorite) {
        dispatch(actions.user.removeRecipeFromFavorites(recipe?.id))
        dispatch(actions.app.setFeedback(t('recipe_unfav')))
      } else {
        dispatch(actions.user.addRecipeToFavorites(recipe?.id))
        dispatch(actions.app.setFeedback(t('recipe_fav')))
      }
    }
  }, [dispatch, isFavorite, recipe?.id, t])

  const toggleFavoritesLogout = useCallback(() => {
    if (recipe?.id) {
      dispatch(
        actions.app.addToAfterLoginJobs(
          (actions.user.updateFavoriteRecipes(
            recipe?.id
          ) as unknown) as PayloadAction
        )
      )
    }
  }, [dispatch, recipe?.id])

  const ssoLoginUrl = useSsoUrl('bookmark')

  const [hasShare, setHasShare] = useState(false)

  useEffect(() => {
    if ((navigator as any)?.share) {
      setHasShare(true)
    }
  }, [])

  const crosslink_recipes = settings?.crosslink_recipes

  const recipeTemplateProps: RecipeTemplateProps = useMemo(() => {
    const earlierPlaningId = earlierPlanningIdFromQuery
    const breadcrumbItems =
      comesFromPlanning || isFromHebdo || earlierPlaningId || isOffline
        ? [
            {
              text: recipe?.name ?? '',
            },
          ]
        : [
            {
              href: Router.getRouteUrl(routes.recipes.name),
              text: t('breadcrumb_recipes'),
            },
            {
              text: recipe?.name ?? '',
            },
          ]
    const breadcrumbConfig = breadcrumbTransformer({
      bgColor: colors.greenMint,
      t,
      hasFamItem: isFromPlanning,
      isFromHebdo,
      lastItems: breadcrumbItems,
    })

    return {
      seoProps: recipeSeoTransformer(
        toDisplayRecipe,
        typeof window !== 'undefined'
          ? `${window.location.origin}${window.location.pathname}`
          : '',
        t
      ),
      coverSectionProps: recipeCoverTransformer({
        recipe: toDisplayRecipe,
        t,
        breadcrumbItems: breadcrumbConfig,
        comesFromPlanning: comesFromPlanning || isFromPlanning,
        isFromHebdo,
        filters,
        day,
        isOffline,
        parentRecipeSlug,
      }),
      introductionSectionProps: recipeIntroductionTransformer({
        recipe: toDisplayRecipe,
        t,
        isSide: !!side,
        selectedSide,
        onSelectSwap,
        isFavorite,
        hideFavorite,
        onAddToFavorites: {
          loginAction: toggleFavorites,
          logoutAction: toggleFavoritesLogout,
        },
        userLoggedIn,
        isFromPlanning,
        isFromHebdo,
        ssoLoginUrl,
        switchCourseAction:
          (isFromPlanning || isFromHebdo) &&
          day &&
          moment &&
          Router.getRouteConfig(routes.switchcourse.name, {
            dayNumber: day,
            moment,
            id: recipe?.id ?? '',
            ...(side && { side: side?.id ?? '' }),
            ...(selectedSide && { tab: selectedSide }),
          }),
        tcVars: {
          chapter1: `action_${TcPageTemplateNames.Recipe}_fam`,
          chapter2: t('breadcrumb_eat_better'),
          chapter3: recipe?.name ?? '',
        },
        hasShare,
        isOffline,
      }),
      ingredientsSectionProps: recipeIngredientsTransformer({
        recipe: toDisplayRecipe,
        t,
        ingredientsPeople,
        onAddPersonHandler,
        onRemovePersonHandler,
        minPortions:
          (constrainedPeopleCount.current && peopleSteps.current) ||
          minPortions?.current ||
          null,
        currentSeason: currentSeason || undefined,
      }),
      stepsSectionProps: recipeStepsTransformer({
        recipe: toDisplayRecipe,
        t,
        isStepByStepOpen,
        toggleStepByStepHandler,
      }),
      menuSectionProps: sameDayRecipes
        ? {
            title: t('other_courses_in_menu'),
            items: sameDayRecipes
              ?.filter(
                (item) =>
                  item.id !== recipe?.id && (side ? item.id !== side.id : true)
              )
              ?.map((item) => {
                return recipeCardTransformer(
                  t,
                  item,
                  day as Day,
                  moment as Moment,
                  undefined,
                  false,
                  true,
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                  isOffline
                )
              }),
          }
        : null,
      othersSectionProps: {
        title: t('recipe_complementary_informations_title'),
        items: crossLinkTransformer(
          crosslink_recipes ?? [],
          t,
          undefined,
          isOffline
        ),
      },
      tagsSectionProps: recipeTagsTransformer(toDisplayRecipe, isOffline),
      mobileBreadcrumbProps: breadcrumbConfig,
      tcProps: tagCommanderTransformer({
        pageName: recipe?.name ?? '',
        tcTemplate: TcPageTemplateNames.Recipe,
        tcCAt1: t('tracking_page_chapter_1'),
        tcCAt2: t('tracking_page_chapter_2'),
        ...(isFromPlanning || isFromHebdo || earlierPlaningId
          ? {
              tcCAt3: t('tracking_page_chapter_3'),
              tcCAt4: t('tracking_page_chapter_4_recipe'),
            }
          : {
              tcCAt3: t('tracking_page_chapter_4_recipe'),
            }),
      }),
      isParentRecipe,
      ...(isParentRecipe && {
        childrenRecipesListSectionProps: {
          recipeCardListProps: childrenRecipesCardListTransformer({
            t,
            recipes: recipe?.children_recipes ?? [],
            parentRecipe: recipe?.slug ?? '',
          }),
        },
      }),
    } as RecipeTemplateProps
  }, [
    earlierPlanningIdFromQuery,
    comesFromPlanning,
    isFromHebdo,
    isOffline,
    recipe?.name,
    recipe?.id,
    recipe?.children_recipes,
    recipe?.slug,
    t,
    isFromPlanning,
    toDisplayRecipe,
    filters,
    day,
    parentRecipeSlug,
    side,
    selectedSide,
    onSelectSwap,
    isFavorite,
    hideFavorite,
    toggleFavorites,
    toggleFavoritesLogout,
    userLoggedIn,
    ssoLoginUrl,
    moment,
    hasShare,
    ingredientsPeople,
    onAddPersonHandler,
    onRemovePersonHandler,
    currentSeason,
    isStepByStepOpen,
    toggleStepByStepHandler,
    sameDayRecipes,
    crosslink_recipes,
    isParentRecipe,
  ])

  return { recipeTemplateProps }
}

export default useRecipe
